/// <reference lib="dom" />
/**
 * gameRenderFrame.ts
 * Pure-DOM per-frame render+physics loop body.
 *
 * Extracted from {@link GameCanvas}'s RAF loop so both the React component
 * (Expo build) and the vanilla DOM game host (Vite build) can share the
 * same draw chain without duplicating it. Owns only the inside of the
 * loop — the loop driver (RAF + delta tracking) lives at each call site.
 *
 * @module gameRenderFrame
 */

import { runPhysicsFrame } from '../engine/physicsFrame';
import { gameState } from '../engine/gameState';
import { spaceState } from '../engine/spaceState';
import { VIEWPORT_WIDTH, VIEWPORT_HEIGHT } from '../../constants/gameConstants';
import {
  BG_COLOR,
  PARALLAX_FAR,
  PARALLAX_MID,
  PARALLAX_NEAR,
} from '../../constants/rendering';
import type { RenderAssets } from './RenderAssets';
import {
  drawParallax,
  drawTileLayers,
  drawExitPortal,
  drawExitPortalFront,
  drawGroundFill,
  drawPitGradients,
  drawGreybox,
} from './webTileDraw';
import { drawSkyBackground, drawSkyForeground, drawSkyShips, setSkySprites } from './webSkyDraw';
import { drawCollectibles } from './webCollectibleDraw';
import { drawEntities } from './webEntityDraw';
import { drawWeaponPickups } from './webWeaponPickupDraw';
import { drawFinale } from './webFinaleDraw';
import { drawSpaceScene } from './webSpaceDraw';
import { finaleState } from '../scenes/FinaleSequence';
import { getUserSetting } from '../services/userSettings';
import { getActiveWorldId } from './renderAssetStore';
import { pickParallax } from './worldParallax';
import { pickTileset } from './worldTileset';

const BG_LAYERS = ['Background', 'Terrain', 'Decor'] as const;
const FG_LAYERS = ['Hazards'] as const;

/**
 * Run one render frame against `ctx`.
 *
 * @param ctx      Canvas 2D rendering context.
 * @param assets   Currently loaded sprite/tileset bundle, or null for greybox.
 * @param deltaMs  Milliseconds since the previous frame. The local rendering
 *                 `dt` is clamped to 0.05s for finale animations; physics has
 *                 its own 0.1s clamp inside {@link runPhysicsFrame}.
 * @param active   Whether physics should advance this frame (paused = false).
 * @param now      Animation timestamp in ms (used for animated collectibles).
 */
export function runGameRenderFrame(
  ctx: CanvasRenderingContext2D,
  assets: RenderAssets | null,
  deltaMs: number,
  active: boolean,
  now: number,
): void {
  if (!active) return;
  runPhysicsFrame(deltaMs, active);

  if (spaceState.isRunning) {
    drawSpaceScene(ctx);
    return;
  }

  const { camera, nova } = gameState;
  let camX = Math.round(camera.x);
  let camY = Math.round(camera.y);
  const dt = Math.min(deltaMs / 1000, 0.05);

  if (finaleState.cameraLocked) {
    camX = Math.round(finaleState.lockedCamX);
    camY = Math.round(finaleState.lockedCamY);
  }

  const shakeX = Math.round(finaleState.shakeX);
  const shakeY = Math.round(finaleState.shakeY);
  if (shakeX !== 0 || shakeY !== 0) {
    camX -= shakeX;
    camY -= shakeY;
  }

  ctx.fillStyle = BG_COLOR;
  ctx.fillRect(0, 0, VIEWPORT_WIDTH, VIEWPORT_HEIGHT);

  const greyboxForced = getUserSetting('useGreyboxMode');

  if (assets && !greyboxForced) {
    setSkySprites(
      assets.cloudLargeImg, assets.cloudMediumImg,
      assets.shipHealthyImg, assets.shipOnFireImg,
      assets.asteroidXLImg, assets.asteroidLGImg,
      assets.asteroidMDImg, assets.asteroidSMImg,
    );

    const parallax = pickParallax(getActiveWorldId(), assets);
    const tileset = pickTileset(getActiveWorldId(), assets);

    drawParallax(ctx, parallax.far, camX, PARALLAX_FAR);
    drawSkyBackground(ctx, camX, dt);
    drawSkyShips(ctx, camX);
    drawParallax(ctx, parallax.mid, camX, PARALLAX_MID);
    drawParallax(ctx, parallax.near, camX, PARALLAX_NEAR);
    drawSkyForeground(ctx, camX);

    if (assets.tiledMap) {
      drawGroundFill(ctx, assets.tiledMap, camX, camY);
      drawTileLayers(ctx, tileset, assets.tiledMap, camX, camY, BG_LAYERS);
      drawPitGradients(ctx, assets.tiledMap, camX, camY);
    }

    drawExitPortal(
      ctx, camX, camY,
      assets.portalActiveImg, assets.portalInactiveImg, assets.portalPedestalImg,
      gameState.level.exitPortalActive ?? false,
    );

    if (assets.tiledMap) {
      drawTileLayers(ctx, tileset, assets.tiledMap, camX, camY, FG_LAYERS);
    }

    drawCollectibles(
      ctx, camX, camY, now,
      tileset, assets.tiledMap,
      assets.starSeedImg, assets.shardImg, assets.heartImg,
      assets.checkpointActiveImg, assets.checkpointInactiveImg,
    );
    drawWeaponPickups(ctx, camX, camY, now);
    drawEntities(ctx, assets, dt, camX, camY);
    drawExitPortalFront(
      ctx, camX, camY,
      assets.portalActiveImg, assets.portalInactiveImg,
      gameState.level.exitPortalActive ?? false,
    );
    drawFinale(
      ctx,
      assets.novaShipImg, assets.flameIdleImg,
      assets.flameCruiseImg, assets.flameBoostImg,
      camX, camY, dt, nova.x, nova.y,
    );
  } else {
    drawGreybox(ctx, camX, camY);
    drawCollectibles(ctx, camX, camY, now, null, null);
    drawWeaponPickups(ctx, camX, camY, now);
  }
}
